import React from "react";
import { Badge, Box, Drawer, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { dashboardFeaturesFor } from "../dashboardFeatures";

const SIDEBAR_WIDTH = 300;

const SidebarMenu: React.FC<{
  selectedFeature: string;
  setSelectedFeature: React.Dispatch<React.SetStateAction<string>>;
  userType: "vendor" | "user";
  isAdmin?: boolean;
  canEditApplication?: boolean;
  /** Unread counts shown beside a menu item, keyed by its path. */
  badges?: Record<string, number>;
  /** Below md the rail is a temporary drawer, opened from the dashboard bar. */
  open: boolean;
  onClose: () => void;
}> = ({
  selectedFeature,
  setSelectedFeature,
  userType,
  isAdmin,
  canEditApplication,
  badges = {},
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("dashboard");
  // A fixed 300px rail leaves 90px of content on a phone, so below md it comes
  // out of the flow entirely rather than being narrowed.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const features = dashboardFeaturesFor(userType, isAdmin, canEditApplication);

  const rail = (
    <Box
      sx={{
        width: { xs: "min(280px, 80vw)", md: SIDEBAR_WIDTH },
        flexShrink: 0,
        height: "100%",
        backgroundColor: theme.palette.warning.main,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        scrollbarWidth: "thin",
      }}
    >
      {features.map((item) => {
        const isSelected = selectedFeature === item.path;

        return (
          <Box
            key={item.path}
            onClick={() => {
              setSelectedFeature(item.path);
              onClose();
            }}
            sx={{
              cursor: "pointer",
              userSelect: "none",
              fontWeight: "bold",
              textTransform: "uppercase",
              textAlign: "center",
              flexShrink: 0,
              fontSize: { xs: "1rem", md: theme.typography.h4.fontSize },
              padding: { xs: theme.spacing(1.5), md: theme.spacing(1.75) },
              backgroundColor: isSelected
                ? theme.palette.primary.main
                : theme.palette.warning.main,
              color: theme.palette.common.white,
              transition: "background-color 0.3s",
              "&:hover": {
                backgroundColor: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.warning.dark,
              },
            }}
          >
            <Badge
              color="error"
              badgeContent={badges[item.path] || null}
              sx={{ "& .MuiBadge-badge": { right: -14, top: 4 } }}
            >
              {t(item.label)}
            </Badge>
          </Box>
        );
      })}
    </Box>
  );

  if (isCompact) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        // The app-wide MuiDrawer override paints a dark 350px panel; this rail
        // brings its own colour and width, so the paper just gets out of the way.
        PaperProps={{ sx: { width: "auto", background: "none" } }}
      >
        {rail}
      </Drawer>
    );
  }

  return rail;
};

export default SidebarMenu;
